import React from 'react';
import ManagedTable, { ManagedColumn, ManagedTableProps } from 'app/common/table/ManagedTable';
import { RegulationDto } from 'app/model/CORE';
import TimeUtils from 'app/common/util/TimeUtils';
import MeasureUtils from 'app/common/util/MeasureUtils';
import useWindowDimensions from 'app/common/hooks/useWindowDimensions';
import { MonitoredMeasure } from 'app/model/SOL49';
import { Flex } from 'app/components/common/Flex';
import Utils from 'app/common/util/Utils';
import { CheckCircleFilled } from '@ant-design/icons';
import { MeasureWarnings } from 'app/components/dashboard/detailed/measures/MeasureWarning';
import { MeasureMoreMenu } from 'app/components/dashboard/detailed/measures/MeasureMoreMenu';
import './MonitoredMeasureTable.less';
import { StandardColumnTooltips } from 'app/components/table/StandardColumnTooltips';

export class MonitoredMeasureColumn extends ManagedColumn<MonitoredMeasure> {}

export const MONITORED_MEASURE_SELECTED_COLUMN = new MonitoredMeasureColumn(
    '',
    '2em',
    () => (
        <div className={'check-circle'}>
            <CheckCircleFilled />
        </div>
    ),
    { name: 'selected' },
);

export const MONITORED_MEASURE_ACTIONS_COLUMN = new MonitoredMeasureColumn(
    '',
    '4em',
    ({ measure }) => (
        <Flex className="more-button" onClick={e => e.stopPropagation()}>
            <MeasureMoreMenu
                measureId={measure?.measureId}
                applicability={measure.applicability}
                tvLoc={MeasureUtils.getTvLocation(measure)}
            />
        </Flex>
    ),
    { name: 'actions', className: () => 'monitored-measure-actions' },
);

export class StandardMonitoredMeasureColumn {
    public static readonly ID = new MonitoredMeasureColumn('ID', '6em', ({ measure }) => measure.measureId.id);
    public static readonly TV = new MonitoredMeasureColumn(
        'TV',
        '6em',
        ({ measure }) => MeasureUtils.getTvLocation(measure).id,
    );
    public static readonly START = new MonitoredMeasureColumn('START', '5em', ({ measure }) =>
        TimeUtils.formatDDHHMM(measure.applicability.from),
    );
    public static readonly END = new MonitoredMeasureColumn('END', '5em', ({ measure }) =>
        TimeUtils.formatDDHHMM(measure.applicability.to),
    );
    public static readonly DELAY = new MonitoredMeasureColumn('DELAY', '4em', ({ delay }) =>
        TimeUtils.millescondsToMinutes(delay),
    );
    public static readonly NORMAL_RATE = new MonitoredMeasureColumn(
        'NR',
        '2em',
        ({ measure }) => MeasureUtils.toRegulationNormalRateString(measure as RegulationDto),
        {
            tooltip: StandardColumnTooltips.REGULATION_NORMAL_RATES,
        },
    );
    public static readonly PENDING_RATE = new MonitoredMeasureColumn(
        'PR',
        '2em',
        ({ measure }) => MeasureUtils.toRegulationPendingRateString(measure as RegulationDto),
        {
            tooltip: StandardColumnTooltips.REGULATION_PENDING_RATES,
        },
    );
    public static readonly RATES = new MonitoredMeasureColumn(
        'RATES',
        '11em',
        ({ measure }) => MeasureUtils.getInitialConstraintsString(measure as RegulationDto),
        { formatter: obj => <span style={{ whiteSpace: 'pre-wrap' }}>{obj}</span> },
    );
    public static readonly WINDOW_WIDTH = new MonitoredMeasureColumn('WW', '2em', ({ measure }) =>
        Utils.leftPadZero(TimeUtils.millescondsToMinutes((measure as RegulationDto).noDelayWindowInMs), 2),
    );
    public static readonly STATE = new MonitoredMeasureColumn('STATE', '4em', ({ measure }) =>
        MeasureUtils.toRegulationStateShortString((measure as RegulationDto).regulationState),
    );
    public static readonly REASON = new MonitoredMeasureColumn(
        'REASON',
        '12em',
        ({ measure }) => (measure as RegulationDto).reason,
    );
    public static readonly LFRS = new MonitoredMeasureColumn(
        'LFRS',
        '4em',
        ({ measure }) => MeasureUtils.toLFRSString(measure as RegulationDto),
        {
            sortingEnabled: false,
            tooltip: StandardColumnTooltips.REGULATION_LFRS,
        },
    );
    public static readonly WARNING = new MonitoredMeasureColumn(
        'WG',
        '1em',
        ({ measure, warnings }) => <MeasureWarnings warnings={warnings} measureId={measure.measureId} />,
        {
            sortingEnabled: false,
        },
    );
}

class MonitoredMeasureTableBase extends ManagedTable<MonitoredMeasure> {}

const MonitoredMeasureTable = (props: ManagedTableProps<MonitoredMeasure>) => {
    const { width } = useWindowDimensions();
    const fontSize = (0.45 * width) / 100;

    return <MonitoredMeasureTableBase fontSize={fontSize} {...props} />;
};

export default MonitoredMeasureTable;
